import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from "react-router-dom";
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from "history";
import ConstituencyCampaignMultiStepForm from './forms/ConstituencyCampaignMultiStepForm';
import Message from './components/Message';
import Home from './pages/Home';
import PrivacyPolicy from './pages/PrivacyPolicy';
import FormProvider from "./context";
import { storePurpose, providePurpose } from "./utils/Purpose";
import { get } from "./utils/Api";
import { update } from "./utils/ValChecker";
import { env } from './env'
import './App.css';

function App() {
    const apiUrl = env.VITE_API_URL ? env.VITE_API_URL : 'https://api.campaigns.org.uk';
    const queryParams = new URLSearchParams(window.location.search);
    const cfk = queryParams.get("cfk")
    const style = queryParams.get("style")
    const embedded = queryParams.get("embedded") === 'true' ? true : false;
    const [data, setData] = useState({
        isLoading: true,
        isLoadCompleted: false,
        isError: false,
        isMissingInfo: false,
        isExpired: false,
        hasUpdatedFavIcon: false,
        session: null,
        provider: {
            url: apiUrl,
            key: cfk,
        }
    });

    if (env.VITE_INSIGHTS_CONNECTION) {
        const browserHistory = createBrowserHistory({ basename: '' });
        var reactPlugin = new ReactPlugin();
        var appInsights = new ApplicationInsights({
            config: {
                connectionString: env.VITE_INSIGHTS_CONNECTION,
                extensions: [reactPlugin],
                extensionConfig: {
                    [reactPlugin.identifier]: { history: browserHistory }
                }
            }
        });
        appInsights.loadAppInsights();
    }

    // Declare effects using useEffect
    useEffect(() => {
        if (env.VITE_ENV && env.VITE_ENV.toLowerCase() !== 'production') {
            console.log(`app.useEffect :: cfk:${cfk}`);
        }

        let hasSessionData = false;

        if (cfk !== null && data.session === null) {
            const storedSessionData = providePurpose(`df.${cfk}`, cfk, true);
            hasSessionData = storedSessionData !== null;
            if (hasSessionData) {
                handleStyle(storedSessionData.tenant.styleName);
            }
            setData((prevFormData) => ({ ...prevFormData, isLoading: false, isError: false, isExpired: false, isMissingInfo: false, session: storedSessionData, isLoadCompleted: true }))
            update(storedSessionData);
        }

        if (cfk != null && !hasSessionData) {
            setData((prevFormData) => ({ ...prevFormData, isLoading: true, isError: false, isMissingInfo: false, session: null }));
            handleSession();
        }
        else if (!hasSessionData) {
            handleStyle('material');
            setData((prevFormData) => ({ ...prevFormData, isLoading: false, isError: cfk !== null, isMissingInfo: true, session: null, isLoadCompleted: true }));
        }
    }, []);

    useEffect(() => {
        if (!data.hasUpdatedFavIcon && data.session && data.session.tenant) {
            if (data.session.tenant.favIconUrl) {
                let link = document.querySelector("link[rel~='icon']");
                if (!link) {
                    link = document.createElement('link');
                    link.rel = 'icon';
                    document.getElementsByTagName('head')[0].appendChild(link);
                }
                link.href = data.session.tenant.favIconUrl;
                setData((prevFormData) => ({ ...prevFormData, hasUpdatedFavIcon: true }));
            }

            if (data.session.tenant.primaryColour) {
                let meta = document.querySelector("meta[name~='theme-color']");
                if (!meta) {
                    meta = document.createElement('meta');
                    meta.name = 'theme-color';
                    document.getElementsByTagName('head')[0].appendChild(meta);
                }
                meta.content = data.session.tenant.primaryColour;
            }    
        }
    }, [data.session]);


    // Declare event handlers or other functions
    const handleStyle = (sessionStyle) => {
        // Set Style for app
        const selectedStyle = style || sessionStyle;

        const stylesheet = document.querySelector('link[id="style"]');
        if (stylesheet && selectedStyle !== 'material') {
            stylesheet.href = 'static/css/styles/' + selectedStyle + '.css';   
        }

        document.body.classList.add(selectedStyle);

        if (embedded) {
            if (embedded && (selectedStyle.includes('dark') || selectedStyle.includes('highcontrast'))) {
                document.body.style.backgroundColor = handleStyleBackgroundColour(selectedStyle);
            } else {
                document.body.style.backgroundColor = '#fff';
            }
        }
    }

    const handleStyleBackgroundColour = (name) => {
        switch (name) {
            case 'bootstrap5-dark':
                return '#212529';
            case 'bootstrap-dark':
                return '#6e6e6e';
            case 'fabric-dark':
                return '#201f1f';
            case 'fluent-dark':
                return '#252423';
            case 'material-dark':
                return '#303030';
            case 'tailwind-dark':
                return '#1f2937';
            case 'highcontrast':
                return '#000';
            default:
                return '';
        }
    }

    const handleSession = async () => {
        const response = await get(`${data.provider.url}/api/v1.0/forms/session?cfk=${encodeURIComponent(data.provider.key)}`);
        if (response.status === 200) {
            handleStyle(response.data.tenant.styleName);
            storePurpose(`df.${cfk}`, response.data, cfk, true);
            update(response.data);
        }
        else if (response.status === 410 && response.data !== null && response.data.tenant) {
            handleStyle(response.data.tenant.styleName);
        }

        setData((prevFormData) => (
            {
                ...prevFormData,
                isLoading: false,
                isLoadCompleted: true,
                isMissingInfo: response.data === null,
                isError: response.status === 400 || response.status === 404 || response.status === 500,
                isExpired: response.status === 410 || response.status === 302,
                session: (response.status === 200 || response.status === 410) ? response.data : null
            }));
    }

    const isTenantDomain = () => {
        return (!window.location.hostname.includes('localhost') && !window.location.hostname.includes('campaigns.org.uk') && !window.location.hostname.includes('cpa-form.pages.dev') && window.location.pathname !== '/privacypolicy');
    }

    const getDomainName = (hostname) => {
        let domain = hostname.match(/^(?:.*?\.)?([a-zA-Z0-9\-_]{3,}\.(?:\w{2,8}|\w{2,4}\.\w{2,4}))$/)[1];
        return domain;
    }

    const hasSessionData = (formType, isMultistep) => {
        return data.session && data.session.type === formType && data.session.isMultiStep === isMultistep;
    }

    const showHomePage = () => {
        return data.isMissingInfo && cfk === null && !isTenantDomain() && window.location.pathname !== '/privacypolicy';
    }

    // Render
    if (data.isLoading) {
        return (
            <div>
                <Message value="Loading..." />
            </div>
        );
    } else if (!embedded && data.isLoadCompleted && !data.isExpired && (data.isMissingInfo || data.isError) && isTenantDomain()) {
        console.log('Redirecting to Tenant Host');
        window.location.href = `${window.location.protocol}//${getDomainName(window.location.hostname)}`;
    } else if (data.isLoadCompleted && !data.isExpired && data.isError && cfk !== null && data.isMissingInfo) {
        return (
            <div>
                <Home isEmbedded={embedded} error="Error missing data, please ensure link is correct and try again." tenant={null} />
            </div>
        );
    } else if (data.isLoadCompleted && !data.isExpired && data.isError) {
        return (
            <div>
                <Home isEmbedded={embedded} error="An error has occurred, please try again or contact support." tenant={null} />
            </div>
        );
    } else if (data.isLoadCompleted && data.isExpired && data.session && data.session.expiredUrl !== '' && data.session.expiredUrl !== null) {
        window.location.href = data.session.expiredUrl;
    } else if (data.isLoadCompleted && data.isExpired) {
        return (
            <div>
                <Home isEmbedded={embedded} error="This link has expired, you may close the browser." tenant={(data.session !== null && data.session.tenant !== null ? data.session.tenant : null)} />
            </div>
        );
    } else if (data && data.isLoadCompleted &&  !data.isExpired && !data.isError) {
        return (
            <FormProvider>
                <Router preserveQueryParams={true}>
                    <div>
                        {(window.location.pathname === "/privacypolicy") && <PrivacyPolicy isEmbedded={embedded} tenant={(data.session !== null && data.session.tenant !== null ? data.session.tenant : null)} />}
                        {hasSessionData('ParliamentCampaign', true) && <ConstituencyCampaignMultiStepForm session={data.session} provider={data.provider} isEmbedded={embedded} />}
                        {showHomePage() && <Home isEmbedded={embedded} tenant={null} />}
                    </div>
                </Router>
            </FormProvider>
        );
    }
}
export default App